import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { SERVICE_TYPES, activeTypeIds, addInterval, formatDate, intervalLabel, moneyInput, monthLabel, parseMoney, todayISO } from "@/lib/services";
import { useContract } from "@/lib/useContract";
import { useServices } from "@/context/ServicesContext";
import { ClientInput } from "@/components/app/ClientInput";

const blank = () => ({ client_name: "", client_phone: "", client_email: "", contact_person: "", service_type: activeTypeIds()[0] || "", execution_date: todayISO(), notes: "", status: "realizado", price: "" });

const StatusToggle = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-2">
    {[["realizado", "Já realizado"], ["agendado", "Apenas agendar"]].map(([v, label]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`status-option-${v}`}
        className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors duration-150 ${value === v ? "border-slate-100 bg-slate-100 text-slate-900" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>
        {label}
      </button>
    ))}
  </div>
);

const Preview = ({ f, contract }) => {
  const t = SERVICE_TYPES[f.service_type];
  if (!f.execution_date || !f.service_type) return null;
  const value = contract ? contract.effective_interval_value : t.value;
  const unit = contract ? contract.effective_interval_unit : t.unit;
  const validity = intervalLabel(value, unit);
  const expiry = addInterval(f.execution_date, value, unit);
  const stopped = contract && contract.status !== "ativo";
  return (
    <div className="flex gap-3 rounded-lg border p-3 text-sm" style={{ borderColor: `${t.hex}55`, background: `${t.hex}12` }} data-testid="schedule-preview">
      <RefreshCw className="mt-0.5 h-4 w-4 shrink-0" style={{ color: t.hex }} />
      {stopped ? <p>Este serviço está <b>{contract.status}</b> na ficha do cliente: nenhum retorno será agendado automaticamente.</p>
        : f.status === "realizado" ? (
          <p>Validade de <b>{validity}</b> (até {formatDate(expiry)}). O próximo serviço será agendado automaticamente para <b>{monthLabel(expiry)}</b>.</p>
        ) : (
          <p>Agendado para <b>{formatDate(f.execution_date)}</b>. Ao marcar como realizado, o próximo será criado pela validade de {validity}.</p>
        )}
    </div>
  );
};

export const ServiceFormDialog = ({ state, onClose }) => {
  const { bump } = useServices();
  const entry = state?.entry;
  const [f, setF] = useState(blank());
  const [names, setNames] = useState([]);
  const [saving, setSaving] = useState(false);
  const [priceTouched, setPriceTouched] = useState(false);
  const contract = useContract(f.client_name, f.service_type, !!state);
  const prefill = useRef({ phone: "", contact: "" });

  useEffect(() => {
    if (!state) return;
    prefill.current = { phone: "", contact: "" };
    setPriceTouched(!!entry);
    setF(entry ? { client_name: entry.client_name, client_phone: "", client_email: "", contact_person: "", service_type: entry.service_type, execution_date: entry.execution_date, notes: entry.notes || "", status: entry.status, price: moneyInput(entry.price) } : { ...blank(), ...state.defaults });
    api.get("/clients").then((r) => setNames(r.data.map((c) => c.name))).catch(() => {});
  }, [state, entry]);

  useEffect(() => {
    const n = f.client_name.trim();
    if (!state || !n) return undefined;
    const t = setTimeout(() => {
      api.get("/clients/contact", { params: { name: n } }).then(({ data }) => setF((p) => {
        if (p.client_name.trim() !== n) return p;
        const old = prefill.current;
        prefill.current = { phone: data.phone || "", contact: data.contact_person || "", email: data.email || "" };
        return { ...p, client_phone: !p.client_phone || p.client_phone === old.phone ? prefill.current.phone : p.client_phone,
          client_email: !p.client_email || p.client_email === old.email ? prefill.current.email : p.client_email,
          contact_person: !p.contact_person || p.contact_person === old.contact ? prefill.current.contact : p.contact_person };
      })).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [f.client_name, state]);

  useEffect(() => {
    if (!state || entry || priceTouched || !f.service_type) return;
    setF((p) => ({ ...p, price: moneyInput(contract ? contract.price : SERVICE_TYPES[p.service_type].default_price) }));
  }, [contract, f.service_type, priceTouched, state, entry]);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.client_name.trim()) return toast.error("Informe o nome do cliente");
    if (!f.service_type) return toast.error("Cadastre um serviço na aba Catálogo primeiro");
    if (!f.execution_date) return toast.error("Informe a data");
    const price = parseMoney(f.price);
    if (Number.isNaN(price)) return toast.error("Valor inválido");
    setSaving(true);
    try {
      const body = { ...f, price };
      const { data } = entry ? await api.put(`/services/${entry.id}`, body) : await api.post("/services", body);
      const nextMsg = data.next ? ` Próximo agendado para ${formatDate(data.next.execution_date)}.` : "";
      toast.success(`${entry ? "Lançamento atualizado." : "Serviço cadastrado."}${nextMsg}`);
      bump();
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-slate-700 bg-slate-900 sm:max-w-lg" data-testid="service-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold">{entry ? "Editar lançamento" : "Novo serviço"}</DialogTitle>
          <DialogDescription>A validade é definida pelo tipo de serviço.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="client_name">Nome do cliente</Label>
            <ClientInput value={f.client_name} onChange={set("client_name")} names={names} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="service_client_phone">Número do cliente (WhatsApp)</Label>
              <Input id="service_client_phone" inputMode="tel" value={f.client_phone} onChange={(e) => set("client_phone")(e.target.value)} placeholder="(92) 90000-0000" className="h-11 bg-slate-950" data-testid="service-client-phone-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="service_contact_person">Nome do contato</Label>
              <Input id="service_contact_person" value={f.contact_person} onChange={(e) => set("contact_person")(e.target.value)} placeholder="Com quem falar" className="h-11 bg-slate-950" data-testid="service-contact-person-input" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="service_client_email">E-mail do cliente (avisos automáticos)</Label>
              <Input id="service_client_email" type="email" value={f.client_email} onChange={(e) => set("client_email")(e.target.value)} placeholder="cliente@email.com" className="h-11 bg-slate-950" data-testid="service-client-email-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Tipo de serviço</Label>
            <Select value={f.service_type} onValueChange={set("service_type")}>
              <SelectTrigger className="h-11 bg-slate-950" data-testid="service-type-select"><SelectValue /></SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-900">
                {Object.entries(SERVICE_TYPES).filter(([k, t]) => t.active || k === f.service_type).map(([k, t]) => (
                  <SelectItem key={k} value={k} data-testid={`service-type-option-${k}`}>
                    <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: t.hex }} />{t.label} · {t.validity}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Situação</Label>
            <StatusToggle value={f.status} onChange={set("status")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="execution_date">{f.status === "realizado" ? "Data de execução" : "Data agendada"}</Label>
            <Input id="execution_date" type="date" value={f.execution_date} onChange={(e) => set("execution_date")(e.target.value)} className="h-11 bg-slate-950" data-testid="execution-date-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="service_price">Valor do atendimento (R$)</Label>
            <Input id="service_price" inputMode="decimal" value={f.price} onChange={(e) => { setPriceTouched(true); set("price")(e.target.value); }} placeholder="Opcional" className="h-11 bg-slate-950" data-testid="service-price-input" />
            {contract && !entry && <p className="text-xs text-slate-500" data-testid="service-price-hint">Valor do contrato deste cliente. Pode ser alterado só neste atendimento.</p>}
          </div>
          <Preview f={f} contract={contract} />
          <div className="space-y-2">
            <Label htmlFor="notes">Observações</Label>
            <Textarea id="notes" rows={3} value={f.notes} onChange={(e) => set("notes")(e.target.value)} placeholder="Opcional" className="bg-slate-950" data-testid="notes-input" />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={onClose} data-testid="service-form-cancel">Cancelar</Button>
            <Button type="submit" disabled={saving} className="rounded-full font-semibold" data-testid="service-form-submit">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
